import { useState, useEffect } from "react"

import {
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material'
import { Link } from "react-router-dom"

import { aiQcService, handleApiError, type AIQCReview, type EventLog } from "../api"

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString()
}

export default function AIQCPage() {
  const [pendingReviews, setPendingReviews] = useState<AIQCReview[]>([])
  const [failedGenerations, setFailedGenerations] = useState<EventLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const loadData = async () => {
    try {
      const [pending, failed] = await Promise.all([
        aiQcService.getPendingReviews(),
        aiQcService.getFailedGenerations(),
      ])
      setPendingReviews(pending)
      setFailedGenerations(failed)
      setError("")
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  if (loading) {
    return (
      <Box sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, p: 3 }}>
      <Card sx={{ height: 'fit-content' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
              AI Reports
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Medical review system for AI-generated reports
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Quick Overview
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current QC status
              </Typography>
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="medium">
                    Pending Review
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {pendingReviews.length}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="medium">
                    Failed Generations
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {failedGenerations.length}
                  </Typography>
                </Box>
                <Box sx={{ pt: 2 }}>
                  <Link to="/ai-qc/pending" style={{ textDecoration: 'none' }}>
                    <Typography variant="body2" color="primary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                      View all pending review →
                    </Typography>
                  </Link>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Common QC tasks
              </Typography>
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Link to="/ai-qc/pending" style={{ textDecoration: 'none' }}>
                  <Paper
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'grey.50' },
                      cursor: 'pointer'
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                      Review Pending Items
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Approve or reject AI reports
                    </Typography>
                  </Paper>
                </Link>
                <Link to="/ai-qc/reviewed" style={{ textDecoration: 'none' }}>
                  <Paper
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'grey.50' },
                      cursor: 'pointer'
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                      View Reviewed Items
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      See completed reviews
                    </Typography>
                  </Paper>
                </Link>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {failedGenerations.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom color="error.main">
                  Failed AI Report Generations
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Orders where AI report generation failed
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Order</TableCell>
                        <TableCell>Trigger</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {failedGenerations.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatDate(log.createdAt)}
                          </TableCell>
                          <TableCell>
                            {log.user ? (
                              <Link to={`/users/${log.user.id}`} style={{ textDecoration: 'none' }}>
                                <Typography variant="body2" color="primary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                                  {log.user.email}
                                </Typography>
                              </Link>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {log.order ? (
                              <Link to={`/orders/${log.order.id}`} style={{ textDecoration: 'none' }}>
                                <Typography variant="body2" color="primary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                                  View order
                                </Typography>
                              </Link>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={log.metadata?.trigger === 'manual' ? 'Manual' : 'Auto'}
                              size="small"
                              color={log.metadata?.trigger === 'manual' ? 'primary' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="error.main">
                              {typeof log.metadata?.reason === 'string' ? log.metadata.reason : log.message}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  )
}
